import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { Bars3Icon, XMarkIcon, HomeIcon } from '@heroicons/react/24/outline'

const NAV_LINKS = [
  { label: 'Funzionalità', to: '/funzionalita' },
  { label: 'Istruttori',   to: '/per-istruttori' },
  { label: 'Centri',       to: '/per-centri' },
  { label: 'Prezzi',       to: '/prezzi' },
  { label: 'Blog',         to: '/blog' },
  { label: 'Contatti',     to: '/contatti' },
]

const FOOTER_LINKS = {
  Piattaforma: [
    { label: 'Funzionalità', to: '/funzionalita' },
    { label: 'Per istruttori', to: '/per-istruttori' },
    { label: 'Per centri', to: '/per-centri' },
    { label: 'Prezzi', to: '/prezzi' },
  ],
  Azienda: [
    { label: 'Chi siamo', to: '/chi-siamo' },
    { label: 'Blog', to: '/blog' },
    { label: 'Partner', to: '/partner' },
    { label: 'Contatti', to: '/contatti' },
  ],
  Legale: [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Termini di servizio', to: '/termini-di-servizio' },
    { label: 'Cookie Policy', to: '/cookie-policy' },
    { label: 'GDPR', to: '/gdpr-info' },
    { label: 'Cancellazione dati', to: '/data-deletion' },
  ],
}

export default function PublicLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore()
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-950">
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo.svg" alt="eDive" className="w-9 h-9 flex-shrink-0" />
            <span className="font-black text-xl tracking-tight" style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}>
              eDive<span className="text-[#0084E6]">{t('auto.layoutsPublicLayout.tech')}</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`hover:text-sky-600 transition-colors ${location.pathname === l.to ? 'text-sky-600 font-medium' : ''}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link
                to="/app"
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:from-sky-400 hover:to-blue-500 transition-all shadow-sm"
              >
                <HomeIcon className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {(user as any)?.firstName ? `Ciao, ${(user as any).firstName}` : 'Torna all\'app'}
                </span>
                <span className="sm:hidden">{t('auto.layoutsPublicLayout.app')}</span>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block text-sm text-gray-600 dark:text-gray-400 hover:text-sky-600 transition-colors">
                  Accedi
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:from-sky-400 hover:to-blue-500 transition-all shadow-sm"
                >
                  Inizia gratis
                </Link>
              </>
            )}

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'}
            >
              {mobileOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileOpen && (
          <div className="md:hidden bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className={`py-2.5 px-3 rounded-lg text-sm transition-colors ${
                  location.pathname === l.to
                    ? 'bg-sky-50 text-sky-600 font-medium dark:bg-sky-900/20 dark:text-sky-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t border-gray-100 dark:border-gray-800 mt-2 pt-3 flex flex-col gap-2">
              {isAuthenticated ? (
                <Link
                  to="/app"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                >
                  <HomeIcon className="w-4 h-4" />
                  {t('auto.layoutsPublicLayout.tornaAllAreaPrivata')}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="py-2.5 px-4 rounded-xl text-sm font-medium text-center border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Accedi
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileOpen(false)}
                    className="py-2.5 px-4 rounded-xl text-sm font-semibold text-center bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                  >
                    Inizia gratis
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-950 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.svg" alt="eDive" className="w-9 h-9 flex-shrink-0" />
                <span className="font-black text-xl tracking-tight text-white" style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}>
                  eDive<span className="text-cyan-400">{t('auto.layoutsPublicLayout.tech')}</span>
                </span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed">
                {t('auto.layoutsPublicLayout.laPiattaformaAllInOnePer')}
              </p>
              {isAuthenticated && (
                <Link
                  to="/app"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-semibold bg-sky-600 hover:bg-sky-500 text-white transition-colors"
                >
                  <HomeIcon className="w-4 h-4" />
                  {t('auto.layoutsPublicLayout.tornaAllApp')}
                </Link>
              )}
            </div>
            {Object.entries(FOOTER_LINKS).map(([group, links]) => (
              <div key={group}>
                <h4 className="text-white/80 font-semibold text-sm mb-4">{group}</h4>
                <ul className="flex flex-col gap-2">
                  {links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className={`text-sm transition-colors ${
                          location.pathname === l.to
                            ? 'text-sky-400'
                            : 'text-white/40 hover:text-white/70'
                        }`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-white/30 text-xs">© {new Date().getFullYear()} eDive. Tutti i diritti riservati.</p>
            <p className="text-white/20 text-xs">{t('auto.layoutsPublicLayout.madeWithInItaly')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
